import { useSignal } from "@preact/signals-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { FaSearch, FaCheckCircle, FaTimesCircle, FaSync, FaHourglassHalf, FaArrowRight, FaCog, FaExclamationTriangle, FaHammer, FaFilter, FaChevronDown } from "react-icons/fa";
import { currencyWithPrefix } from "../../../utils/currencyUtils";
import { formatDateToBR } from "../../../utils/DateUtils";
import { formatDocNumber } from "../../../utils/documentUtils";
import DateFilter from "../../molecules/DateFilter";
import PageHeader from "../../molecules/PageHeader";
import Loading from "../../ions/Loading";
import { OperationsSummaryModal } from "../../molecules/OperationsSummaryModal";
import NewTooltip from "../../atoms/NewTooltip";
import appConfig from "../../../config/app-config";
import { Auth } from "aws-amplify";

interface Liquidation {
    contract_modality: string;
    contract_number: string;
    liquidation_value_received: number;
    debt_value: number;
    debt_expire_date: string;
    liquidation_bank: string;
    payer_doc_number: string;
    repeat_times: number;
}

type LiquidationStatus = 'LOADING' | 'PENDING' | 'SENDING' | 'PROCESSING' | 'REGISTERED' | 'ERROR' | 'LOAD_FAILURE';

interface StatusInfo {
    status: LiquidationStatus;
    errorMessage?: string;
}

const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

const LIQUIDATION_BATCH_SIZE = 20;
const FORCE_MODE_DATE = '2021-10-05'; // data da Fundação da Agrolend

const getAuthHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
};

const LiquidationsList: React.FC = () => {
    const liquidations = useSignal<Liquidation[]>([]);
    const isLoading = useSignal(false);
    const [liquidationDate, setLiquidationDate] = useState(new Date().toISOString().split('T')[0]);
    const [previousDate, setPreviousDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [statusMap, setStatusMap] = useState<Map<number, StatusInfo>>(new Map());
    const [isSending, setIsSending] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [forceMode, setForceMode] = useState(false);
    const [selectedContracts, setSelectedContracts] = useState<Set<string>>(new Set());
    const [showContractFilter, setShowContractFilter] = useState(false);
    const contractFilterRef = useRef<HTMLDivElement>(null);
    const statusCheckIntervals = useState<Map<number, NodeJS.Timeout>>(new Map())[0];

    let cachedHeaders: Record<string, string> | null = null;
    let cachedHeadersTime = 0;
    const getCachedAuthHeaders = async () => {
        const now = Date.now();
        if (!cachedHeaders || now - cachedHeadersTime > 5 * 60 * 1000) {
            cachedHeaders = await getAuthHeaders();
            cachedHeadersTime = now;
        }
        return cachedHeaders;
    };

    const fetchLiquidations = async () => {
        if (!liquidationDate) {
            toast.error("Selecione uma data");
            return;
        }

        // Ativa modo força se a data for FORCE_MODE_DATE
        if (liquidationDate === FORCE_MODE_DATE) {
            setForceMode(true);
            setLiquidationDate(previousDate);
            toast.info("Modo força ativado");
            return;
        } else {
            setForceMode(false);
            setPreviousDate(liquidationDate);
        }

        isLoading.value = true;
        if (!API_URL || !API_KEY) {
            toast.error("Configuração da API de liquidações não encontrada");
            return;
        }
        isLoading.value = true;
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/debt-register?liquidation_date=${liquidationDate}`, {
                headers
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`Erro: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response:', data);
            liquidations.value = data.liquidations || [];
            setSelectedRows(new Set());
            setStatusMap(new Map());
            setSelectedContracts(new Set());

            if (liquidations.value.length === 0) {
                toast.info("Nenhuma liquidação encontrada para esta data");
            } else {
                const totalItems = liquidations.value.reduce((sum, item) => sum + (item.repeat_times || 1), 0);
                toast.success(`${liquidations.value.length} grupo(s) | ${totalItems} item(s) encontrado(s)`);
            }

        } catch (error: any) {
            console.error('Fetch Error:', error);
            toast.error(`Erro ao buscar liquidações: ${error.message}`);
            liquidations.value = [];
        } finally {
            isLoading.value = false;
            if (liquidations.value.length > 0) {
                fetchAllStatuses();
            }
        }
    };

    useEffect(() => {
        return () => {
            statusCheckIntervals.forEach(intervalId => clearInterval(intervalId));
            statusCheckIntervals.clear();
        };
    }, [statusCheckIntervals]);

    const buildBatchBody = (items: Liquidation[], startIdx: number) => ({
        liquidations: items.map((item, idx) => ({
            seqno: startIdx + idx + 1,
            contract_modality: item.contract_modality,
            liquidation_date: liquidationDate,
            liquidation_value_received: String(item.liquidation_value_received),
            contract_number: item.contract_number,
            liquidation_bank: item.liquidation_bank,
            debt_value: String(item.debt_value),
            debt_expire_date: item.debt_expire_date,
            payer_doc_number: item.payer_doc_number,
            repeat_times: item.repeat_times
        }))
    });

    const processBatchResponse = (data: any) => {
        data.liquidation_status_list.forEach((statusItem: any) => {
            const index = statusItem.seqno - 1;
            setStatusMap(prev => new Map(prev).set(index, {
                status: statusItem.status || 'PENDING',
                errorMessage: statusItem.error_message
            }));
            if (statusItem.status === 'PROCESSING') {
                startStatusPolling(index);
            }
        });
    };

    const setBatchFailure = (startIdx: number, endIdx: number) => {
        for (let i = startIdx; i < endIdx; i++) {
            setStatusMap(prev => new Map(prev).set(i, { status: 'LOAD_FAILURE' }));
        }
    };

    const fetchAllStatuses = async () => {
        const newStatusMap = new Map<number, StatusInfo>();
        liquidations.value.forEach((_, index) => {
            newStatusMap.set(index, { status: 'LOADING' });
        });
        setStatusMap(newStatusMap);

        const totalItems = liquidations.value.length;
        const batches = Math.ceil(totalItems / LIQUIDATION_BATCH_SIZE);

        const headers = await getAuthHeaders();

        const batchPromises = Array.from({ length: batches }, async (_, batchIndex) => {
            const startIdx = batchIndex * LIQUIDATION_BATCH_SIZE;
            const endIdx = Math.min(startIdx + LIQUIDATION_BATCH_SIZE, totalItems);
            const batchItems = liquidations.value.slice(startIdx, endIdx);

            try {
                const body = buildBatchBody(batchItems, startIdx);
                const response = await fetch(`${API_URL}/debt-register-status`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(body)
                });

                if (response.ok) {
                    processBatchResponse(await response.json());
                } else {
                    setBatchFailure(startIdx, endIdx);
                    toast.warning(`Erro ao verificar status do lote ${batchIndex + 1}`);
                }
            } catch (error) {
                console.error('Error fetching batch statuses:', error);
                setBatchFailure(startIdx, endIdx);
                toast.warning(`Erro ao verificar status do lote ${batchIndex + 1}`);
            }
        });

        await Promise.all(batchPromises);
    };

    const startStatusPolling = (index: number) => {
        if (statusCheckIntervals.has(index)) {
            clearInterval(statusCheckIntervals.get(index));
        }

        const intervalId = setInterval(async () => {
            await checkSingleStatus(index);
        }, 10000);

        statusCheckIntervals.set(index, intervalId);
    };

    const checkSingleStatus = async (index: number) => {
        const item = liquidations.value[index];
        if (!item) return;

        try {
            const body = {
                liquidations: [{
                    seqno: index + 1,
                    contract_modality: item.contract_modality,
                    liquidation_date: liquidationDate,
                    liquidation_value_received: String(item.liquidation_value_received),
                    contract_number: item.contract_number,
                    liquidation_bank: item.liquidation_bank,
                    debt_value: String(item.debt_value),
                    debt_expire_date: item.debt_expire_date,
                    payer_doc_number: item.payer_doc_number,
                    repeat_times: item.repeat_times
                }]
            };

            const response = await fetch(`${API_URL}/debt-register-status`, {
                method: 'POST',
                headers: await getCachedAuthHeaders(),
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                const statusItem = data.liquidation_status_list[0];

                if (statusItem && statusItem.status !== 'PROCESSING') {
                    setStatusMap(prev => new Map(prev).set(index, {
                        status: statusItem.status,
                        errorMessage: statusItem.error_message
                    }));

                    const intervalId = statusCheckIntervals.get(index);
                    if (intervalId) {
                        clearInterval(intervalId);
                        statusCheckIntervals.delete(index);
                    }
                }
            } else {
                console.error('Error checking status:', response.status);
                setStatusMap(prev => new Map(prev).set(index, {
                    status: 'ERROR',
                    errorMessage: `Erro ao consultar status (${response.status})`
                }));
                const intervalId = statusCheckIntervals.get(index);
                if (intervalId) {
                    clearInterval(intervalId);
                    statusCheckIntervals.delete(index);
                }
            }
        } catch (error) {
            console.error('Error checking status:', error);
            setStatusMap(prev => new Map(prev).set(index, {
                status: 'ERROR',
                errorMessage: 'Erro ao consultar status'
            }));
            const intervalId = statusCheckIntervals.get(index);
            if (intervalId) {
                clearInterval(intervalId);
                statusCheckIntervals.delete(index);
            }
        }
    };

    const sendLiquidation = async (index: number, item: Liquidation, force: boolean = false) => {
        if (!API_URL || !API_KEY) {
            toast.error("Configuração da API de liquidações não encontrada");
            setStatusMap(prev => new Map(prev).set(index, { status: 'ERROR', errorMessage: 'Configuração não encontrada' }));
            return;
        }
        setStatusMap(prev => new Map(prev).set(index, { status: 'SENDING' }));

        try {
            const payload = force ? { ...item, force: true } : item;
            const response = await fetch(`${API_URL}/debt-register`, {
                method: 'POST',
                headers: await getAuthHeaders(),
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setStatusMap(prev => new Map(prev).set(index, { status: 'PROCESSING' }));
                setSelectedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(index);
                    return newSet;
                });
                toast.success("Enviado com sucesso");

                setTimeout(() => {
                    startStatusPolling(index);
                }, 10000);
            } else {
                const errorData = await response.json();
                setStatusMap(prev => new Map(prev).set(index, { status: 'ERROR', errorMessage: errorData.message || 'Erro no envio' }));
                toast.error(`Erro ao enviar: ${response.status}`);
            }
        } catch (error: any) {
            setStatusMap(prev => new Map(prev).set(index, { status: 'ERROR', errorMessage: error.message }));
            toast.error(`Erro: ${error.message}`);
        }
    };

    const sendSelected = async () => {
        if (selectedRows.size === 0) {
            toast.error("Nenhum item selecionado");
            return;
        }

        setIsSending(true);
        for (const index of Array.from(selectedRows)) {
            await sendLiquidation(index, liquidations.value[index]);
        }
        setIsSending(false);
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) {
            const validIndexes = visibleIndices.filter(i => {
                const status = statusMap.get(i)?.status;
                return status === 'PENDING' || status === 'ERROR' || status === 'LOAD_FAILURE';
            });
            setSelectedRows(new Set(validIndexes));
        } else {
            setSelectedRows(new Set());
        }
    };

    const isRowDisabled = (index: number): boolean => {
        const status = statusMap.get(index)?.status;
        return status === 'LOADING' || status === 'SENDING' || status === 'PROCESSING' || status === 'REGISTERED';
    };

    const toggleRow = (index: number) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const formatModality = (modality: string) => {
        const map: Record<string, string> = {
            'CESSION_WITH_RETURN': 'Empréstimo PJ',
            'CESSION_WITHOUT_RETURN': 'Sessão sem Regresso',
            'COMMON_CPRF': 'CPRF Varejo'
        };
        return map[modality] || '-';
    };

    const formatCurrency = (value: number) => currencyWithPrefix(value);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return formatDateToBR(dateString + 'T00:00:00');
    };

    const distinctContracts = useMemo(() => {
        const set = new Set<string>();
        liquidations.value.forEach(l => {
            if (l.contract_number) set.add(l.contract_number);
        });
        return Array.from(set).sort();
    }, [liquidations.value]);

    const visibleIndices = useMemo(() => {
        return liquidations.value
            .map((_, i) => i)
            .filter(i => {
                if (selectedContracts.size === 0) return true;
                return selectedContracts.has(liquidations.value[i].contract_number);
            });
    }, [liquidations.value, selectedContracts]);

    const visibleIndexSet = useMemo(() => new Set(visibleIndices), [visibleIndices]);

    useEffect(() => {
        setSelectedRows(prev => {
            let changed = false;
            const newSet = new Set<number>();
            prev.forEach(idx => {
                if (visibleIndexSet.has(idx)) newSet.add(idx);
                else changed = true;
            });
            return changed ? newSet : prev;
        });
    }, [visibleIndexSet]);

    useEffect(() => {
        if (!showContractFilter) return;
        const handler = (e: MouseEvent) => {
            if (contractFilterRef.current && !contractFilterRef.current.contains(e.target as Node)) {
                setShowContractFilter(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showContractFilter]);

    const toggleContract = (contract: string) => {
        setSelectedContracts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(contract)) newSet.delete(contract);
            else newSet.add(contract);
            return newSet;
        });
    };

    const summary = useMemo(() => {
        console.log('Calculando summary liquidations:', liquidations.value);
        const visibleLiqs = visibleIndices.map(i => liquidations.value[i]);

        const total = visibleLiqs.reduce((sum, liq) => {
            const value = Number(liq.liquidation_value_received) || 0;
            const repeatTimes = liq.repeat_times || 1;
            return sum + (value * repeatTimes);
        }, 0);

        const totalRegistered = visibleIndices.reduce((sum, index) => {
            const liq = liquidations.value[index];
            const statusInfo = statusMap.get(index);
            if (statusInfo?.status === 'REGISTERED') {
                const value = Number(liq.liquidation_value_received) || 0;
                const repeatTimes = liq.repeat_times || 1;
                return sum + (value * repeatTimes);
            }
            return sum;
        }, 0);

        const bankTotals = visibleLiqs.reduce((acc, liq) => {
            const bank = liq.liquidation_bank || 'Não informado';
            if (!acc[bank]) acc[bank] = 0;
            const value = Number(liq.liquidation_value_received) || 0;
            const repeatTimes = liq.repeat_times || 1;
            acc[bank] += value * repeatTimes;
            return acc;
        }, {} as Record<string, number>);

        console.log('Summary liquidations calculado:', { total, totalRegistered, bankTotals });
        return {
            total,
            totalRegistered,
            bankTotals: Object.entries(bankTotals).map(([bank, total]) => ({ bank, total }))
        };
    }, [liquidations.value, statusMap, visibleIndices]);

    const selectedTotal = useMemo(() => {
        return Array.from(selectedRows).reduce((sum, idx) => {
            const liq = liquidations.value[idx];
            if (liq) {
                const value = Number(liq.liquidation_value_received) || 0;
                const repeatTimes = liq.repeat_times || 1;
                return sum + (value * repeatTimes);
            }
            return sum;
        }, 0);
    }, [selectedRows, liquidations.value]);

    const getStatusIcon = (index: number) => {
        const statusInfo = statusMap.get(index);
        if (!statusInfo) return <span className="text-gray-400">-</span>;

        const { status, errorMessage } = statusInfo;

        switch (status) {
            case 'LOADING':
                return (
                    <NewTooltip content="Carregando status">
                        <FaSync className="text-blue-600 inline animate-spin" />
                    </NewTooltip>
                );
            case 'PENDING':
                return (
                    <NewTooltip content="Aguardando envio">
                        <FaHourglassHalf className="text-gray-500 inline" />
                    </NewTooltip>
                );
            case 'SENDING':
                return (
                    <NewTooltip content="Enviando...">
                        <FaArrowRight className="text-blue-600 inline" />
                    </NewTooltip>
                );
            case 'PROCESSING':
                return (
                    <NewTooltip content="Processando...">
                        <FaCog className="text-yellow-600 inline animate-spin" />
                    </NewTooltip>
                );
            case 'REGISTERED':
                return (
                    <NewTooltip content="Registrado">
                        <FaCheckCircle className="text-green-600 inline" />
                    </NewTooltip>
                );
            case 'ERROR':
                return (
                    <NewTooltip content={errorMessage || 'Erro'}>
                        <FaTimesCircle className="text-red-600 inline cursor-help" />
                    </NewTooltip>
                );
            case 'LOAD_FAILURE':
                return (
                    <NewTooltip content="Erro ao carregar status">
                        <FaExclamationTriangle className="text-yellow-600 inline cursor-help" />
                    </NewTooltip>
                );
            default:
                return <span className="text-gray-400">-</span>;
        }
    };

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {isLoading.value && <Loading />}

                <PageHeader
                    title="Lançamento de Baixas/Liquidações"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Financeiro', url: '#' },
                        { label: 'Liquidações', url: '#' }
                    ]}
                >
                    <div className="pt-4">
                        {liquidations.value.length > 0 && (
                            <div className="flex items-center gap-4">
                                <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium text-sm">
                                    {selectedRows.size} grupo(s) | {Array.from(selectedRows).reduce((sum, idx) => sum + (liquidations.value[idx]?.repeat_times || 1), 0)} item(s) selecionado(s)
                                </span>
                                <span className="px-3 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded font-medium text-sm">
                                    {currencyWithPrefix(selectedTotal)}
                                </span>
                                <button
                                    onClick={sendSelected}
                                    disabled={selectedRows.size === 0 || isSending}
                                    className="text-white py-2 px-4 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Enviar Selecionados
                                </button>
                            </div>
                        )}
                    </div>
                </PageHeader>

                <div className={`p-4 rounded-lg mb-4 flex items-center gap-4 justify-between ${forceMode ? 'bg-red-100 dark:bg-red-900/40 border-2 border-red-500' : 'bg-gray-50 dark:bg-slate-700'} ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="flex items-center gap-4">
                        {forceMode && (
                            <span className="px-3 py-2 bg-red-600 text-white rounded font-bold text-sm flex items-center gap-2">
                                <FaHammer />
                                MODO FORÇA
                            </span>
                        )}
                        <DateFilter
                            label="Data de Liquidação:"
                            value={liquidationDate}
                            onChange={setLiquidationDate}
                        />
                        <button
                            onClick={fetchLiquidations}
                            disabled={isLoading.value || isSending}
                            className="text-white py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <FaSearch />
                            Buscar
                        </button>
                        {liquidations.value.length > 0 && distinctContracts.length > 0 && (
                            <div className="relative" ref={contractFilterRef}>
                                <button
                                    onClick={() => setShowContractFilter(v => !v)}
                                    className="text-gray-700 dark:text-gray-200 py-2 px-3 rounded-lg bg-white dark:bg-slate-600 border border-gray-300 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-slate-500 flex items-center gap-2 text-sm"
                                >
                                    <FaFilter />
                                    Contratos
                                    {selectedContracts.size > 0 && (
                                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded text-xs">
                                            {selectedContracts.size}
                                        </span>
                                    )}
                                    <FaChevronDown className="text-xs" />
                                </button>
                                {showContractFilter && (
                                    <div className="absolute z-50 mt-1 w-72 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg">
                                        <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 dark:border-slate-600 text-xs">
                                            <button
                                                onClick={() => setSelectedContracts(new Set(distinctContracts))}
                                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                            >
                                                Selecionar todos
                                            </button>
                                            <button
                                                onClick={() => setSelectedContracts(new Set())}
                                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                            >
                                                Limpar
                                            </button>
                                        </div>
                                        <div className="max-h-60 overflow-y-auto">
                                            {distinctContracts.map(c => (
                                                <label
                                                    key={c}
                                                    className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-600 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedContracts.has(c)}
                                                        onChange={() => toggleContract(c)}
                                                        className="w-4 h-4 cursor-pointer"
                                                    />
                                                    <span className="ml-2">{c}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {liquidations.value.length > 0 && (
                            <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium text-sm">
                                Total: {visibleIndices.length} grupo(s) | {visibleIndices.reduce((sum, idx) => sum + (liquidations.value[idx]?.repeat_times || 1), 0)} item(s)
                            </span>
                        )}
                    </div>
                    {liquidations.value.length > 0 && (
                        <button
                            onClick={() => setShowSummaryModal(true)}
                            className="text-white py-2 px-4 rounded-lg bg-yellow-600 hover:bg-yellow-700 flex items-center gap-2"
                        >
                            Resumo Operações
                        </button>
                    )}
                </div>

                {liquidations.value.length === 0 ? (
                    <div className="text-center py-20 text-gray-500 dark:text-gray-400 italic">
                        Selecione uma data e clique em Buscar para visualizar liquidações
                    </div>
                ) : (
                    <div className={`max-h-[calc(100vh-200px)] overflow-x-auto bg-white dark:bg-slate-800 shadow border border-gray-200 dark:border-slate-600 ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                        <table className="min-w-full text-sm table-auto auto-cols-auto">
                            <thead className="text-xs text-slate-500 uppercase bg-gray-50 sticky top-0 shadow-md dark:bg-slate-800 dark:text-gray-50 z-50">
                                <tr>
                                    <th className="px-2 py-2 text-center border-b w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.size > 0 && selectedRows.size === visibleIndices.filter(i => !isRowDisabled(i)).length}
                                            onChange={(e) => toggleSelectAll(e.target.checked)}
                                            className="w-4 h-4 cursor-pointer align-middle"
                                        />
                                    </th>
                                    <th className="px-2 py-2 text-center border-b w-12">#</th>
                                    <th className="px-2 py-2 text-left border-b">Modalidade</th>
                                    <th className="px-2 py-2 text-left border-b">Contrato</th>
                                    <th className="px-2 py-2 text-center border-b">Vencimento</th>
                                    <th className="px-2 py-2 text-right border-b">Valor Dívida</th>
                                    <th className="px-2 py-2 text-right border-b">Valor Liquidação</th>
                                    <th className="px-2 py-2 text-left border-b">Banco</th>
                                    <th className="px-2 py-2 text-left border-b">Sacado</th>
                                    <th className="px-2 py-2 text-center border-b">Repetições</th>
                                    <th className="px-2 py-2 text-center border-b w-24" colSpan={forceMode ? 2 : 1}>Ação</th>
                                    <th className="px-2 py-2 text-center border-b w-32">Status</th>
                                </tr>
                            </thead>
                            <tbody className="overflow-y-auto">
                                {visibleIndices.map((index) => {
                                    const item = liquidations.value[index];
                                    return (
                                    <tr
                                        key={index}
                                        className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                                    >
                                        <td className="px-2 py-2 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.has(index)}
                                                onChange={() => toggleRow(index)}
                                                disabled={isRowDisabled(index)}
                                                className="w-4 h-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed align-middle"
                                            />
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-900 dark:text-gray-100">{index + 1}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatModality(item.contract_modality)}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{item.contract_number || '-'}</td>
                                        <td className="px-2 py-2 text-center text-gray-900 dark:text-gray-100">{formatDate(item.debt_expire_date)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.debt_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.liquidation_value_received)}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{item.liquidation_bank || '-'}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatDocNumber(item.payer_doc_number)}</td>
                                        <td className="px-2 py-2 text-center text-gray-900 dark:text-gray-100">{item.repeat_times || 1}</td>
                                        <td className="px-2 text-center">
                                            <button
                                                onClick={() => sendLiquidation(index, item)}
                                                disabled={isRowDisabled(index)}
                                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Enviar
                                            </button>
                                        </td>
                                        {forceMode && (
                                            <td className="px-2 text-center">
                                                <button
                                                    onClick={() => sendLiquidation(index, item, true)}
                                                    className="px-3 py-1 bg-gray-900 text-white text-sm rounded hover:bg-gray-800 flex items-center gap-1 mx-auto"
                                                >
                                                    <FaHammer className="text-xs" />
                                                    Enviar
                                                </button>
                                            </td>
                                        )}
                                        <td className="px-2 py-2 text-center">
                                            {getStatusIcon(index)}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <OperationsSummaryModal
                isOpen={showSummaryModal}
                onClose={() => setShowSummaryModal(false)}
                totalLiquidated={summary.total}
                totalRegistered={summary.totalRegistered}
                bankTotals={summary.bankTotals}
            />
        </div>
    );
};

export default LiquidationsList;
